import React, { useState, useEffect, useCallback, useRef } from "react";
import JerseyNumber from "../utils/JerseyNumber";
import { useNavigate } from "react-router-dom";
import { useGame } from "../context/GameContext";
import {
  getTeamStyle,
  getTextColorForAccent,
  isDarkModeActive,
} from "../utils/teamColors";

// ─── Constants ────────────────────────────────────────────────────────────────

const STATS = [
  {
    key: "points",
    label: "+2",
    rawPoints: 2,
    makeStat: "twoPm",
    hotkey: "2",
    color: "bg-emerald-500 hover:bg-emerald-400 active:bg-emerald-600",
  },
  {
    key: "points3",
    label: "+3",
    rawPoints: 3,
    makeStat: "threePm",
    hotkey: "3",
    color: "bg-emerald-700 hover:bg-emerald-600 active:bg-emerald-800",
  },
  {
    key: "points1",
    label: "+1",
    rawPoints: 1,
    makeStat: "ftMade",
    hotkey: "1",
    color: "bg-emerald-300 hover:bg-emerald-200 active:bg-emerald-400",
  },
  {
    key: "rebounds",
    label: "REB",
    rawPoints: 0,
    hotkey: "r",
    color: "bg-sky-500 hover:bg-sky-400 active:bg-sky-600",
  },
  {
    key: "assists",
    label: "AST",
    rawPoints: 0,
    hotkey: "a",
    color: "bg-violet-500 hover:bg-violet-400 active:bg-violet-600",
  },
  {
    key: "personalFouls",
    label: "PF",
    rawPoints: 0,
    hotkey: "f",
    color: "bg-orange-600 hover:bg-orange-500 active:bg-orange-700",
  },
  {
    key: "technicalFouls",
    label: "TF",
    rawPoints: 0,
    hotkey: "t",
    color: "bg-red-700 hover:bg-red-600 active:bg-red-800",
  },
];

const BONUS_THRESHOLD = 7;
const DBL_BONUS_THRESHOLD = 10;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getFoulStatus = (player) => {
  const pf = player.gamePersonalFouls ?? 0;
  const tf = player.gameTechnicalFouls ?? 0;
  if (pf >= 6 || tf >= 2) return "dq";
  if (pf === 5 || tf === 1) return "warning";
  return "ok";
};

const getLastName = (fullName) => {
  const parts = fullName.trim().split(" ");
  return parts.length > 1
    ? parts[parts.length - 1].toUpperCase()
    : fullName.toUpperCase();
};

const getFoulBonusInfo = (fouls) => {
  if (fouls >= DBL_BONUS_THRESHOLD)
    return { label: "DBL BONUS", color: "bg-red-600 text-white" };
  if (fouls >= BONUS_THRESHOLD)
    return { label: "BONUS", color: "bg-orange-500 text-white" };
  return null;
};

// ─── PlayerRow with inline stat buttons ──────────────────────────────────────

const PlayerRow = ({
  player,
  teamNum,
  onStatClick,
  teamColors,
  dragHandleProps,
  isDragging,
  lastAction,
}) => {
  const foulStatus = getFoulStatus(player);
  const isDQ = foulStatus === "dq";

  // Check if this player was the last action
  const isLastAction =
    lastAction?.playerId === player.id && lastAction?.teamNum === teamNum;

  return (
    <div
      className={`flex items-center gap-1 px-2 py-1 rounded-lg transition-all select-none
                ${isDQ ? "opacity-40" : "bg-white dark:bg-gray-800"}
                ${isDragging ? "opacity-50 scale-95" : ""}
                ${isLastAction ? "ring-2 ring-yellow-400" : ""}`}
    >
      {/* Drag handle */}
      <div
        {...dragHandleProps}
        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 px-1 touch-none"
        title="Drag to reorder"
      >
        ⋮⋮
      </div>

      {/* Jersey + Name */}
      <div className="flex items-center gap-1 min-w-[80px] shrink-0">
        <JerseyNumber
          number={player.jersey_number ?? "–"}
          color={teamColors.accentHex}
          size="sm"
          mode="inline"
        />
        <span className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">
          {getLastName(player.name)}
        </span>
      </div>

      {/* Foul warning */}
      {foulStatus === "dq" && (
        <span className="text-[10px] font-black text-red-500 shrink-0">DQ</span>
      )}
      {foulStatus === "warning" && (
        <span className="text-[10px] font-bold text-orange-500 shrink-0">
          ⚠
        </span>
      )}

      {/* Stat buttons - inline */}
      <div className="flex gap-0.5 flex-1 justify-end">
        {STATS.map((stat) => (
          <button
            key={stat.key}
            onClick={() => !isDQ && onStatClick(teamNum, player, stat)}
            disabled={isDQ}
            className={`${stat.color} text-white font-bold text-[10px] px-1.5 py-1 rounded
                            transition-all active:scale-95 disabled:opacity-30 disabled:cursor-not-allowed
                            touch-manipulation min-w-[32px]`}
            title={`${stat.label} (${stat.hotkey})`}
          >
            {stat.label}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── TeamColumn with drag-and-drop ────────────────────────────────────────────

const TeamColumn = ({
  team,
  players,
  teamNum,
  foulsH1,
  foulsH2,
  opponentFoulsH1,
  opponentFoulsH2,
  currentHalf,
  onStatClick,
  onReorder,
  teamTimeouts,
  takeTimeout,
  lastAction,
}) => {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDragOverIndex(index);
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const newPlayers = [...players];
    const [draggedPlayer] = newPlayers.splice(draggedIndex, 1);
    newPlayers.splice(dropIndex, 0, draggedPlayer);

    onReorder(teamNum, newPlayers);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const opponentFouls = currentHalf === 1 ? opponentFoulsH1 : opponentFoulsH2;
  const bonusInfo = getFoulBonusInfo(opponentFouls);
  const teamStyle = getTeamStyle(team);

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div
        className="flex items-center justify-between px-2 py-1.5 text-white shrink-0"
        style={teamStyle.headerStyle}
      >
        <div className="font-bold text-sm truncate flex-1">{team?.name}</div>

        {bonusInfo && (
          <span
            className={`text-[9px] font-black px-1.5 py-0.5 rounded-full ${bonusInfo.color} mx-1`}
          >
            {bonusInfo.label}
          </span>
        )}

        <div className="text-[10px] font-semibold mx-1 shrink-0">
          TO: {teamTimeouts}
        </div>

        <button
          className="text-[10px] bg-white/20 hover:bg-white/30 px-1.5 py-0.5 rounded font-semibold"
          onClick={() => takeTimeout(teamNum)}
        >
          TO
        </button>
      </div>

      {/* Players - scrollable if needed but should fit */}
      <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900/50 px-1 py-1">
        <div className="flex flex-col gap-0.5">
          {players.map((player, index) => (
            <div
              key={player.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`${dragOverIndex === index && draggedIndex !== index ? "border-t-2 border-blue-500" : ""}`}
            >
              <PlayerRow
                player={player}
                teamNum={teamNum}
                onStatClick={onStatClick}
                teamColors={teamStyle}
                isDragging={draggedIndex === index}
                lastAction={lastAction}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// ─── LiveGame (root) ──────────────────────────────────────────────────────────

const LiveGame = () => {
  const navigate = useNavigate();
  const { activeGame, recordStat, advanceHalf, finishGame, takeTimeout } =
    useGame();

  const [lastAction, setLastAction] = useState(null);
  const [team1Order, setTeam1Order] = useState([]);
  const [team2Order, setTeam2Order] = useState([]);

  // Initialize player order when game loads
  useEffect(() => {
    if (!activeGame) return;
    if (activeGame.team1Players?.length > 0 && team1Order.length === 0) {
      setTeam1Order(activeGame.team1Players);
    }
    if (activeGame.team2Players?.length > 0 && team2Order.length === 0) {
      setTeam2Order(activeGame.team2Players);
    }
  }, [activeGame, team1Order.length, team2Order.length]);

  // Update orders when players change (stats update)
  useEffect(() => {
    if (!activeGame) return;

    // Preserve order but update player data
    if (activeGame.team1Players?.length > 0) {
      setTeam1Order((prev) => {
        if (prev.length === 0) return activeGame.team1Players;
        return prev.map(
          (p) => activeGame.team1Players.find((ap) => ap.id === p.id) || p,
        );
      });
    }
    if (activeGame.team2Players?.length > 0) {
      setTeam2Order((prev) => {
        if (prev.length === 0) return activeGame.team2Players;
        return prev.map(
          (p) => activeGame.team2Players.find((ap) => ap.id === p.id) || p,
        );
      });
    }
  }, [activeGame, activeGame?.team1Players, activeGame?.team2Players]);

  // ─── Wake lock ────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!activeGame) return;
    let wakeLock = null;
    const acquire = async () => {
      try {
        if ("wakeLock" in navigator) {
          wakeLock = await navigator.wakeLock.request("screen");
        }
      } catch (_) {
        /* not fatal */
      }
    };
    acquire();
    const reacquire = () => {
      if (document.visibilityState === "visible") acquire();
    };
    document.addEventListener("visibilitychange", reacquire);
    return () => {
      document.removeEventListener("visibilitychange", reacquire);
      wakeLock?.release().catch(() => {});
    };
  }, [activeGame]);

  // ─── Stat recording ───────────────────────────────────────────────────────
  const handleStatClick = useCallback(
    (teamNum, player, stat) => {
      const statName =
        stat.key === "points3" || stat.key === "points1" ? "points" : stat.key;
      recordStat(teamNum, player.id, statName, stat.rawPoints);
      if (stat.makeStat) recordStat(teamNum, player.id, stat.makeStat, 0);

      setLastAction({
        label: stat.label,
        playerName: getLastName(player.name),
        teamNum,
        playerId: player.id,
        stat,
        statName,
      });
    },
    [recordStat],
  );

  // ─── Undo ─────────────────────────────────────────────────────────────────
  const lastActionRef = useRef(lastAction);
  useEffect(() => {
    lastActionRef.current = lastAction;
  }, [lastAction]);

  const handleUndo = useCallback(() => {
    const action = lastActionRef.current;
    if (!action) return;

    const { teamNum, playerId, stat, statName } = action;

    if (stat.rawPoints !== 0) {
      recordStat(teamNum, playerId, statName, -stat.rawPoints);
    } else {
      recordStat(teamNum, playerId, statName, 0, -1);
    }
    if (stat.makeStat) recordStat(teamNum, playerId, stat.makeStat, 0, -1);

    setLastAction(null);
  }, [recordStat]);

  // ─── Keyboard shortcuts ───────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName)) return;

      const key = e.key.toLowerCase();

      if (key === "u") {
        e.preventDefault();
        handleUndo();
        return;
      }

      if (key === "escape") {
        setLastAction(null);
        return;
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleUndo]);

  // ─── Player reordering ────────────────────────────────────────────────────
  const handleReorder = useCallback((teamNum, newOrder) => {
    if (teamNum === 1) {
      setTeam1Order(newOrder);
    } else {
      setTeam2Order(newOrder);
    }
  }, []);

  // ─── Game controls ────────────────────────────────────────────────────────
  const handleFinishGame = async () => {
    if (!window.confirm("Are you sure you want to finish this game?")) return;
    const result = await finishGame();
    if (result.success) {
      alert("Game finished and saved successfully!");
      navigate("/standings");
    } else {
      alert("Error finishing game: " + result.error?.message);
    }
  };

  // ─── No active game guard ─────────────────────────────────────────────────
  if (!activeGame) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-8 text-center">
        <h2 className="text-2xl font-bold mb-4 dark:text-white">
          No Active Game
        </h2>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Please start a game first from the scorer setup page.
        </p>
        <button
          onClick={() => navigate("/scorer")}
          className="bg-orange-600 text-white px-6 py-3 rounded-xl font-semibold hover:bg-orange-700 touch-manipulation"
        >
          Go to Scorer Setup
        </button>
      </div>
    );
  }

  const h = activeGame.half;

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-100 dark:bg-gray-900">
      {/* ══ Scoreboard ════════════════════════════════════════════════════ */}
      <div className="bg-white dark:bg-gray-900 text-gray-900 dark:text-white px-3 py-2 shrink-0 border-b border-gray-200 dark:border-gray-800">
        <div className="flex items-center justify-between">
          {/* Team 1 */}
          <div className="text-center flex-1">
            {(() => {
              const ts1 = getTeamStyle(activeGame.team1);
              const isDark = isDarkModeActive();
              // Use primaryHex for score display; ensure it contrasts against the white/dark scoreboard bg
              const scoreBg = isDark ? "#111827" : "#ffffff";
              const scoreColor =
                getTextColorForAccent(scoreBg, isDark) === "#ffffff"
                  ? ts1.accentHex // dark bg → use accent as-is (likely light enough)
                  : ts1.primaryHex; // light bg → use primary
              return (
                <>
                  <div
                    className="text-[10px] font-bold uppercase tracking-wider truncate"
                    style={{ color: ts1.accentHex }}
                  >
                    {activeGame.team1?.name}
                  </div>
                  <div
                    className="text-4xl font-black tabular-nums leading-none"
                    style={{ color: scoreColor }}
                  >
                    {activeGame.team1Score}
                  </div>
                </>
              );
            })()}
          </div>

          {/* Center */}
          <div className="flex flex-col items-center px-2 shrink-0">
            <div className="text-[9px] font-bold uppercase tracking-wider text-gray-400">
              {h === 1 ? "1st Half" : h === 2 ? "2nd Half" : `OT${h - 2}`}
            </div>
            <div className="text-gray-500 font-bold text-sm">–</div>
            <div
              className={`text-[8px] px-1.5 py-0.5 rounded-full font-bold ${
                activeGame.isScrimmage
                  ? "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                  : "bg-orange-100 dark:bg-orange-900/60 text-orange-600 dark:text-orange-300"
              }`}
            >
              {activeGame.isScrimmage ? "SCRIM" : "REG"}
            </div>
          </div>

          {/* Team 2 */}
          <div className="text-center flex-1">
            {(() => {
              const ts2 = getTeamStyle(activeGame.team2);
              const isDark = isDarkModeActive();
              const scoreBg = isDark ? "#111827" : "#ffffff";
              const scoreColor =
                getTextColorForAccent(scoreBg, isDark) === "#ffffff"
                  ? ts2.accentHex
                  : ts2.primaryHex;
              return (
                <>
                  <div
                    className="text-[10px] font-bold uppercase tracking-wider truncate"
                    style={{ color: ts2.accentHex }}
                  >
                    {activeGame.team2?.name}
                  </div>
                  <div
                    className="text-4xl font-black tabular-nums leading-none"
                    style={{ color: scoreColor }}
                  >
                    {activeGame.team2Score}
                  </div>
                </>
              );
            })()}
          </div>
        </div>
      </div>

      {/* ══ Undo bar ══════════════════════════════════════════════════════ */}
      <div className="bg-gray-50 dark:bg-gray-800 px-3 py-1 shrink-0 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="text-[10px] text-gray-500 dark:text-gray-400">
            {lastAction ? (
              <>
                Last:{" "}
                <strong className="text-gray-700 dark:text-gray-200">
                  {lastAction.label} → {lastAction.playerName}
                </strong>
              </>
            ) : (
              "Tap stat buttons to record · Drag players to reorder"
            )}
          </div>
          <button
            onClick={handleUndo}
            disabled={!lastAction}
            className={`text-[10px] font-bold px-2 py-1 rounded transition-all
                            ${
                              lastAction
                                ? "bg-orange-600 text-white hover:bg-orange-500"
                                : "bg-gray-300 dark:bg-gray-700 text-gray-500 cursor-not-allowed"
                            }`}
            title="Undo (U)"
          >
            ↩ UNDO
          </button>
        </div>
      </div>

      {/* ══ Teams side-by-side ════════════════════════════════════════════ */}
      <div className="flex-1 grid grid-cols-2 gap-2 p-2 overflow-hidden">
        {/* Team 1 */}
        {activeGame.team1 && (
          <TeamColumn
            team={activeGame.team1}
            players={team1Order}
            teamNum={1}
            foulsH1={activeGame.team1FoulsH1}
            foulsH2={activeGame.team1FoulsH2}
            opponentFoulsH1={activeGame.team2FoulsH1}
            opponentFoulsH2={activeGame.team2FoulsH2}
            currentHalf={h}
            onStatClick={handleStatClick}
            onReorder={handleReorder}
            teamTimeouts={activeGame.team1TimeoutsLeft}
            takeTimeout={takeTimeout}
            lastAction={lastAction}
          />
        )}

        {/* Team 2 */}
        {activeGame.team2 && (
          <TeamColumn
            team={activeGame.team2}
            players={team2Order}
            teamNum={2}
            foulsH1={activeGame.team2FoulsH1}
            foulsH2={activeGame.team2FoulsH2}
            opponentFoulsH1={activeGame.team1FoulsH1}
            opponentFoulsH2={activeGame.team1FoulsH2}
            currentHalf={h}
            onStatClick={handleStatClick}
            onReorder={handleReorder}
            teamTimeouts={activeGame.team2TimeoutsLeft}
            takeTimeout={takeTimeout}
            lastAction={lastAction}
          />
        )}
      </div>

      {/* ══ Bottom action bar ═════════════════════════════════════════════ */}
      <div className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-3 py-2 flex gap-2 shrink-0">
        <button
          onClick={advanceHalf}
          className="flex-1 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 
                        dark:hover:bg-gray-600 text-gray-900 dark:text-white py-2 rounded-lg font-bold text-sm
                        touch-manipulation active:scale-95 transition-all"
        >
          {h === 1 ? "▶ 2nd Half" : `▶ OT${h - 1}`}
        </button>
        <button
          onClick={handleFinishGame}
          className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white py-2
                        rounded-lg font-bold text-sm touch-manipulation active:scale-95 transition-all"
        >
          ✓ Finish
        </button>
      </div>
    </div>
  );
};

export default LiveGame;
